import { useState, useEffect, useRef, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { getToken } from '@shared/services/api';
import { useDialog } from '@shared/hooks/useDialog';
import { Card, Badge, Button } from '@shared/components/ui';
import {
  Download,
  Clock,
  Plus,
  Trash2,
  Play,
  Pause,
  BarChart2,
  Users,
  Database,
} from 'lucide-react';

interface ReportConfig {
  id: string;
  name: string;
  description: string;
  type: 'monthly' | 'quarterly' | 'custom';
}

interface ReportData {
  month: string;
  new_customers: number;
  churned_customers: number;
  revenue: number;
  support_tickets: number;
  total_backups: number;
  storage_used_gb: number;
  active_agents: number;
}

interface ScheduledReport {
  id: string;
  report_type: string;
  frequency: 'monthly' | 'quarterly';
  recipients: string[];
  enabled: boolean;
  next_run: string;
}

const REPORT_TYPES: ReportConfig[] = [
  {
    id: 'monthly-summary',
    name: 'Monthly Platform Summary',
    description: 'Overview of platform metrics for a specific month',
    type: 'monthly',
  },
  {
    id: 'quarterly-summary',
    name: 'Quarterly Platform Summary',
    description: 'Comprehensive quarterly report with trends',
    type: 'quarterly',
  },
];

function parseReportRecipients(value: string): string[] {
  return value.split(',').map((recipient) => recipient.trim());
}

function isValidReportRecipient(recipient: string): boolean {
  return /^[^\s@,]+@[^\s@.,]+(?:\.[^\s@.,]+)+$/.test(recipient);
}

export default function PlatformReports() {
  const { t, i18n } = useTranslation('admin');
  const tLabel = (key: string) => t(`platformReports.${key}`);
  const { showConfirm } = useDialog();
  const [selectedReportType, setSelectedReportType] = useState<string>('monthly-summary');
  const [selectedMonth, setSelectedMonth] = useState<string>(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [scheduledReports, setScheduledReports] = useState<ScheduledReport[]>([]);
  const [scheduledLoading, setScheduledLoading] = useState(true);
  const [scheduledError, setScheduledError] = useState(false);
  const [scheduledActionError, setScheduledActionError] = useState<string | null>(null);
  const [scheduledActionPending, setScheduledActionPending] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [newReportRecipients, setNewReportRecipients] = useState<string>('');
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [scheduleError, setScheduleError] = useState<string | null>(null);
  const [newReportFrequency, setNewReportFrequency] = useState<'monthly' | 'quarterly'>('monthly');
  const [error, setError] = useState<string | null>(null);
  const scheduleModalWasOpen = useRef(false);
  const token = getToken();
  const recipientEmails = parseReportRecipients(newReportRecipients);
  const hasValidRecipients =
    recipientEmails.length > 0 && recipientEmails.every(isValidReportRecipient);
  const recipientHelpText =
    newReportRecipients.trim().length > 0 && !hasValidRecipients
      ? t('platformReports.recipientsInvalid')
      : t('platformReports.recipientsHint');

  useEffect(() => {
    fetchScheduledReports();
  }, []);

  useEffect(() => {
    if (showScheduleModal) {
      setScheduleError(null);
      scheduleModalWasOpen.current = true;
      document.getElementById('scheduled-report-type')?.focus();
    } else if (scheduleModalWasOpen.current) {
      document.getElementById('schedule-report-trigger')?.focus();
      scheduleModalWasOpen.current = false;
    }
  }, [showScheduleModal]);

  const handleScheduleModalKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      if (savingSchedule) return;
      setShowScheduleModal(false);
      return;
    }

    if (event.key !== 'Tab') return;

    const focusable = Array.from(
      event.currentTarget.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => element.offsetParent !== null);
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (focusable.length === 0) {
      event.preventDefault();
      event.currentTarget.focus();
    } else if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const fetchScheduledReports = async () => {
    setScheduledLoading(true);
    setScheduledError(false);
    try {
      const res = await fetch('/api/v1/admin/reports/scheduled', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load scheduled reports');
      const data = await res.json();
      const reports = data.reports ?? [];
      if (!Array.isArray(reports)) throw new Error('Invalid scheduled reports response');
      setScheduledReports(reports);
    } catch {
      setScheduledError(true);
    } finally {
      setScheduledLoading(false);
    }
  };

  const generateReport = async () => {
    setGenerating(true);
    setError(null);
    try {
      const res = await fetch(
        `/api/v1/admin/reports/generate?type=${selectedReportType}&month=${selectedMonth}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (res.ok) setReportData(await res.json());
      else setError(t('platformReports.errors.generate'));
    } catch {
      setError(t('platformReports.errors.connect'));
    } finally {
      setGenerating(false);
    }
  };

  const downloadPDF = async () => {
    if (!reportData) return;
    setDownloading(true);
    setError(null);
    try {
      const res = await fetch(
        `/api/v1/admin/reports/pdf?type=${selectedReportType}&month=${selectedMonth}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `platform-report-${getReportPeriodFileSuffix()}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      } else setError(t('platformReports.errors.download'));
    } catch {
      setError(t('platformReports.errors.download'));
    } finally {
      setDownloading(false);
    }
  };

  const scheduleReport = async () => {
    if (!hasValidRecipients || savingSchedule) return;

    setSavingSchedule(true);
    setScheduleError(null);
    try {
      const res = await fetch('/api/v1/admin/reports/schedule', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          report_type: selectedReportType,
          frequency: newReportFrequency,
          recipients: recipientEmails,
        }),
      });
      if (res.ok) {
        fetchScheduledReports();
        setShowScheduleModal(false);
        setNewReportRecipients('');
      } else setScheduleError(t('platformReports.errors.schedule'));
    } catch {
      setScheduleError(t('platformReports.errors.schedule'));
    } finally {
      setSavingSchedule(false);
    }
  };

  const toggleReportEnabled = async (id: string, enabled: boolean) => {
    if (scheduledActionPending) return;
    setScheduledActionPending(true);
    setScheduledActionError(null);
    try {
      const res = await fetch(`/api/v1/admin/reports/scheduled/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled }),
      });
      if (!res.ok) throw new Error('Update failed');
      await fetchScheduledReports();
    } catch {
      setScheduledActionError(t('platformReports.errors.updateScheduled'));
    } finally {
      setScheduledActionPending(false);
    }
  };

  const deleteScheduledReport = async (id: string) => {
    if (scheduledActionPending) return;
    const confirmed = await showConfirm(
      t('platformReports.confirmDeleteTitle'),
      t('platformReports.confirmDeleteMessage'),
      'warning'
    );
    if (!confirmed) return;
    setScheduledActionPending(true);
    setScheduledActionError(null);
    try {
      const res = await fetch(`/api/v1/admin/reports/scheduled/${encodeURIComponent(id)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Delete failed');
      await fetchScheduledReports();
    } catch {
      setScheduledActionError(t('platformReports.errors.deleteScheduled'));
    } finally {
      setScheduledActionPending(false);
    }
  };

  const getMonthOptions = () => {
    const options = [];
    const now = new Date();
    const isQuarterly = selectedReportType === 'quarterly-summary';
    const currentPeriodStartMonth = isQuarterly
      ? Math.floor(now.getMonth() / 3) * 3
      : now.getMonth();
    for (let i = 0; i < 12; i++) {
      const date = new Date(
        now.getFullYear(),
        currentPeriodStartMonth - i * (isQuarterly ? 3 : 1),
        1
      );
      if (isQuarterly) {
        options.push({
          value: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`,
          label: t('platformReports.quarterLabel', {
            quarter: Math.floor(date.getMonth() / 3) + 1,
            year: date.getFullYear(),
          }),
        });
        continue;
      }
      options.push({
        value: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`,
        label: date.toLocaleDateString(i18n.language, { year: 'numeric', month: 'long' }),
      });
    }
    return options;
  };

  const getSelectedPeriodLabel = () => {
    const date = new Date(`${selectedMonth}-01T00:00:00`);
    if (selectedReportType === 'quarterly-summary') {
      return t('platformReports.quarterLabel', {
        quarter: Math.floor(date.getMonth() / 3) + 1,
        year: date.getFullYear(),
      });
    }
    return new Intl.DateTimeFormat(i18n.language, { year: 'numeric', month: 'long' }).format(date);
  };

  const getReportPeriodFileSuffix = () => {
    if (selectedReportType !== 'quarterly-summary') return selectedMonth;
    const date = new Date(`${selectedMonth}-01T00:00:00`);
    return `${date.getFullYear()}-Q${Math.floor(date.getMonth() / 3) + 1}`;
  };

  return (
    <>
      <div className="space-y-8 animate-in fade-in duration-700">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <h1 className="app-page-title">{t('platformReports.title')}</h1>
            <p className="text-text-muted-light dark:text-text-muted-dark font-medium mt-1">
              {t('platformReports.description')}
            </p>
          </div>
        </div>

        {/* Report Generator */}
        <Card className="p-6">
          <div className="flex flex-col xl:flex-row gap-4 items-end mb-6">
            <div className="flex-1 w-full">
              <label
                htmlFor="platform-report-type"
                className="text-xs font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark mb-2 block"
              >
                {t('platformReports.reportType')}
              </label>
              <select
                id="platform-report-type"
                value={selectedReportType}
                onChange={(e) => {
                  const reportType = e.target.value;
                  setSelectedReportType(reportType);
                  setReportData(null);
                  setError(null);
                  const selectedDate = new Date(`${selectedMonth}-01T00:00:00`);
                  if (reportType === 'quarterly-summary') {
                    const quarterStartMonth = Math.floor(selectedDate.getMonth() / 3) * 3 + 1;
                    setSelectedMonth(
                      `${selectedDate.getFullYear()}-${String(quarterStartMonth).padStart(2, '0')}`
                    );
                  } else {
                    const now = new Date();
                    const monthsAgo =
                      (now.getFullYear() - selectedDate.getFullYear()) * 12 +
                      now.getMonth() -
                      selectedDate.getMonth();
                    if (monthsAgo > 11) {
                      setSelectedMonth(
                        `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
                      );
                    }
                  }
                }}
                disabled={generating}
                className="w-full min-h-11 px-4 py-3 bg-surface-card-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark rounded-xl text-sm font-bold focus:ring-2 focus:ring-brand-500 outline-none"
              >
                {REPORT_TYPES.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.id === 'monthly-summary'
                      ? tLabel('monthlySummary')
                      : tLabel('quarterlySummary')}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex-1 w-full">
              <label
                htmlFor="platform-report-period"
                className="text-xs font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark mb-2 block"
              >
                {t('platformReports.period')}
              </label>
              <select
                id="platform-report-period"
                value={selectedMonth}
                onChange={(e) => {
                  setSelectedMonth(e.target.value);
                  setReportData(null);
                  setError(null);
                }}
                disabled={generating}
                className="w-full min-h-11 px-4 py-3 bg-surface-card-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark rounded-xl text-sm font-bold focus:ring-2 focus:ring-brand-500 outline-none"
              >
                {getMonthOptions().map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>

            <Button
              onClick={generateReport}
              isLoading={generating}
              className="w-full xl:w-auto h-[46px]"
            >
              {t('platformReports.generate')}
            </Button>
          </div>

          {error && (
            <div
              role="alert"
              className="p-4 bg-state-danger-bg-light dark:bg-state-danger-bg-dark text-state-danger-light dark:text-state-danger-dark font-bold border border-state-danger-light/20 dark:border-state-danger-dark/20 rounded-xl"
            >
              {error}
            </div>
          )}

          {reportData && (
            <div className="pt-6 border-t border-surface-border-light dark:border-surface-border-dark animate-in fade-in">
              <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:justify-between sm:items-center mb-6">
                <h3 className="text-lg font-black text-slate-900 dark:text-white">
                  {t('platformReports.results', { period: getSelectedPeriodLabel() })}
                </h3>
                <Button variant="outline" size="sm" onClick={downloadPDF} isLoading={downloading}>
                  <Download size={16} className="mr-2" /> PDF
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
                <StatCard
                  label={t('platformReports.newCustomers')}
                  value={reportData.new_customers}
                  icon={<Users />}
                  color="text-state-info-light dark:text-state-info-dark"
                  bg="bg-state-info-bg-light dark:bg-state-info-bg-dark"
                />
                <StatCard
                  label={t('platformReports.churned')}
                  value={reportData.churned_customers}
                  icon={<Users />}
                  color="text-state-danger-light dark:text-state-danger-dark"
                  bg="bg-state-danger-bg-light dark:bg-state-danger-bg-dark"
                />
                <StatCard
                  label={t('platformReports.revenue')}
                  value={new Intl.NumberFormat(i18n.language, {
                    style: 'currency',
                    currency: 'USD',
                  }).format(reportData.revenue)}
                  icon={<BarChart2 />}
                  color="text-state-ok-light dark:text-state-ok-dark"
                  bg="bg-state-ok-bg-light dark:bg-state-ok-bg-dark"
                />
                <StatCard
                  label={t('platformReports.totalBackups')}
                  value={reportData.total_backups}
                  icon={<Database />}
                  color="text-accent-text-light dark:text-accent-text-dark"
                  bg="bg-accent-soft-light dark:bg-accent-soft-dark"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="p-4 bg-surface-2-light dark:bg-surface-2-dark rounded-xl border border-surface-border-light dark:border-surface-border-dark">
                  <div className="flex justify-between mb-2">
                    <span className="text-sm font-bold text-text-muted-light dark:text-text-muted-dark">
                      {t('platformReports.storageUsed')}
                    </span>
                    <span className="text-sm font-black text-slate-900 dark:text-white">
                      {new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 2 }).format(
                        reportData.storage_used_gb
                      )}{' '}
                      GB
                    </span>
                  </div>
                  <div className="w-full bg-surface-border-light dark:bg-surface-border-dark h-2 rounded-full overflow-hidden">
                    <div className="bg-state-warn-light dark:bg-state-warn-dark h-full w-3/4"></div>
                  </div>
                </div>
                <div className="p-4 bg-surface-2-light dark:bg-surface-2-dark rounded-xl border border-surface-border-light dark:border-surface-border-dark">
                  <div className="flex justify-between mb-2">
                    <span className="text-sm font-bold text-text-muted-light dark:text-text-muted-dark">
                      {t('platformReports.activeAgents')}
                    </span>
                    <span className="text-sm font-black text-slate-900 dark:text-white">
                      {reportData.active_agents}
                    </span>
                  </div>
                  <div className="w-full bg-surface-border-light dark:bg-surface-border-dark h-2 rounded-full overflow-hidden">
                    <div className="bg-brand-600 dark:bg-brand-400 h-full w-1/2"></div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Card>

        {/* Scheduled Reports */}
        <Card className="p-6">
          <div className="flex flex-col gap-3 mb-6 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-xl font-black text-slate-900 dark:text-white flex items-center gap-2">
              <Clock className="text-brand-600 dark:text-brand-400" />{' '}
              {t('platformReports.scheduledReports')}
            </h2>
            <Button
              id="schedule-report-trigger"
              className="w-full whitespace-nowrap sm:w-auto"
              size="sm"
              onClick={() => setShowScheduleModal(true)}
            >
              <Plus size={16} className="mr-2" /> {t('platformReports.scheduleNew')}
            </Button>
          </div>

          {scheduledLoading ? (
            <div
              role="status"
              aria-live="polite"
              className="text-center py-12 text-text-subtle-light dark:text-text-subtle-dark font-medium"
            >
              {t('platformReports.loadingScheduled')}
            </div>
          ) : scheduledError ? (
            <div role="alert" className="flex flex-col items-center gap-3 py-8 text-center">
              <p className="font-medium text-state-danger-light dark:text-state-danger-dark">
                {t('platformReports.errors.loadScheduled')}
              </p>
              <Button variant="outline" onClick={fetchScheduledReports}>
                {t('platformReports.errors.retry')}
              </Button>
            </div>
          ) : scheduledReports.length === 0 ? (
            <div className="text-center py-12 text-text-subtle-light dark:text-text-subtle-dark font-medium">
              {t('platformReports.emptyScheduled')}
            </div>
          ) : (
            <div className="space-y-3">
              {scheduledActionError && (
                <p
                  role="alert"
                  className="rounded-lg bg-state-danger-bg-light p-3 text-sm text-state-danger-light dark:bg-state-danger-bg-dark dark:text-state-danger-dark"
                >
                  {scheduledActionError}
                </p>
              )}
              {scheduledReports.map((report) => (
                <div
                  key={report.id}
                  className="p-4 border border-surface-border-light dark:border-surface-border-dark rounded-xl flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 group hover:bg-surface-2-light dark:hover:bg-surface-2-dark transition-colors"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-3 mb-1">
                      <h3 className="break-words font-bold text-slate-900 dark:text-white">
                        {report.report_type === 'monthly-summary'
                          ? tLabel('monthlySummary')
                          : report.report_type === 'quarterly-summary'
                            ? tLabel('quarterlySummary')
                            : report.report_type}
                      </h3>
                      <Badge variant={report.enabled ? 'success' : 'neutral'}>
                        {report.enabled ? t('platformReports.active') : t('platformReports.paused')}
                      </Badge>
                      <Badge variant="info" className="uppercase">
                        {t(`platformReports.frequencies.${report.frequency}`)}
                      </Badge>
                    </div>
                    <div className="text-xs text-text-muted-light dark:text-text-muted-dark font-medium flex flex-col gap-2 lg:flex-row lg:flex-wrap lg:gap-4">
                      <span className="min-w-0 break-all">
                        {t('platformReports.recipients')}: {report.recipients.join(', ')}
                      </span>
                      <span>
                        {t('platformReports.nextRun')}:{' '}
                        {new Intl.DateTimeFormat(i18n.language).format(new Date(report.next_run))}
                      </span>
                    </div>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={scheduledActionPending}
                      aria-label={
                        report.enabled
                          ? t('platformReports.pauseReport')
                          : t('platformReports.enableReport')
                      }
                      onClick={() => toggleReportEnabled(report.id, !report.enabled)}
                    >
                      {report.enabled ? <Pause size={16} /> : <Play size={16} />}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={scheduledActionPending}
                      aria-label={t('platformReports.deleteReport')}
                      onClick={() => deleteScheduledReport(report.id)}
                    >
                      <Trash2
                        size={16}
                        className="text-state-danger-light dark:text-state-danger-dark"
                      />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Schedule Modal */}
        {showScheduleModal && (
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in"
            role="dialog"
            aria-modal="true"
            aria-labelledby="scheduled-report-dialog-title"
            tabIndex={-1}
            onKeyDown={handleScheduleModalKeyDown}
          >
            <Card className="w-full max-w-lg max-h-[calc(100vh-2rem)] overflow-y-auto shadow-elevated">
              <div className="p-6 sm:p-8 space-y-6">
                <h2
                  id="scheduled-report-dialog-title"
                  className="text-2xl font-black text-slate-900 dark:text-white"
                >
                  {t('platformReports.scheduleTitle')}
                </h2>

                <div className="space-y-4">
                  {scheduleError && (
                    <p
                      role="alert"
                      className="rounded-lg border border-state-danger-light/30 bg-state-danger-bg-light px-4 py-3 text-sm text-state-danger-light dark:border-state-danger-dark/30 dark:bg-state-danger-bg-dark dark:text-state-danger-dark"
                    >
                      {scheduleError}
                    </p>
                  )}
                  <div>
                    <label
                      htmlFor="scheduled-report-type"
                      className="text-xs font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark mb-2 block"
                    >
                      {t('platformReports.reportType')}
                    </label>
                    <select
                      id="scheduled-report-type"
                      disabled={savingSchedule}
                      className="w-full min-h-11 px-4 py-3 bg-surface-card-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark rounded-xl text-sm font-bold focus:ring-2 focus:ring-brand-500 outline-none"
                      value={selectedReportType}
                      onChange={(e) => setSelectedReportType(e.target.value)}
                    >
                      {REPORT_TYPES.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.id === 'monthly-summary'
                            ? tLabel('monthlySummary')
                            : tLabel('quarterlySummary')}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label
                      htmlFor="scheduled-report-frequency"
                      className="text-xs font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark mb-2 block"
                    >
                      {t('platformReports.frequency')}
                    </label>
                    <select
                      id="scheduled-report-frequency"
                      disabled={savingSchedule}
                      className="w-full min-h-11 px-4 py-3 bg-surface-card-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark rounded-xl text-sm font-bold focus:ring-2 focus:ring-brand-500 outline-none"
                      value={newReportFrequency}
                      onChange={(e) => setNewReportFrequency(e.target.value as any)}
                    >
                      <option value="monthly">{t('platformReports.frequencies.monthly')}</option>
                      <option value="quarterly">
                        {t('platformReports.frequencies.quarterly')}
                      </option>
                    </select>
                  </div>

                  <div>
                    <label
                      htmlFor="scheduled-report-recipients"
                      className="text-xs font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark mb-2 block"
                    >
                      {t('platformReports.recipients')}
                    </label>
                    <input
                      id="scheduled-report-recipients"
                      disabled={savingSchedule}
                      type="email"
                      multiple
                      placeholder={t('platformReports.recipientsPlaceholder')}
                      value={newReportRecipients}
                      onChange={(e) => setNewReportRecipients(e.target.value)}
                      aria-invalid={newReportRecipients.length > 0 && !hasValidRecipients}
                      aria-describedby="scheduled-report-recipients-hint"
                      className="w-full min-h-11 px-4 py-3 bg-surface-card-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark rounded-xl text-sm font-medium focus:ring-2 focus:ring-brand-500 outline-none"
                    />
                    <p
                      id="scheduled-report-recipients-hint"
                      className={`mt-2 text-xs ${newReportRecipients.length > 0 && !hasValidRecipients ? 'text-state-danger-light dark:text-state-danger-dark' : 'text-text-muted-light dark:text-text-muted-dark'}`}
                    >
                      {recipientHelpText}
                    </p>
                  </div>
                </div>

                <div className="flex flex-col gap-3 pt-4 sm:flex-row">
                  <Button
                    variant="secondary"
                    className="w-full whitespace-nowrap sm:flex-1"
                    onClick={() => setShowScheduleModal(false)}
                    disabled={savingSchedule}
                  >
                    {t('platformReports.cancel')}
                  </Button>
                  <Button
                    className="w-full whitespace-nowrap sm:flex-1"
                    onClick={scheduleReport}
                    isLoading={savingSchedule}
                    disabled={!hasValidRecipients || savingSchedule}
                  >
                    {t('platformReports.saveSchedule')}
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        )}
      </div>
    </>
  );
}

function StatCard({ label, value, icon, color, bg }: any) {
  return (
    <div className={`p-4 rounded-xl ${bg} flex flex-col justify-between`}>
      <div className={`mb-4 ${color}`}>{icon}</div>
      <div>
        <p className="text-2xl font-black text-slate-900 dark:text-white">{value}</p>
        <p className={`text-xs font-bold uppercase tracking-wider ${color}`}>{label}</p>
      </div>
    </div>
  );
}
